import { Check } from 'lucide-react';
import { DropdownMenu as MenuPrimitive } from 'radix-ui';
import { Fragment, useId, type ReactNode } from 'react';

import { cn } from '@/lib/cn';

/**
 * Menu — the dropdown action list (`FE-04.10`).
 *
 * `card-back.md` uses it for the comment action menu (`FR-COLLAB-002` AC2/AC3,
 * `FR-COLLAB-003` AC3) and the card-back overflow; `entry-surfaces.md` for the
 * workspace switcher and the account menu. `finding-things.md` uses it for none
 * of its three surfaces and says so as a finding — a filter panel, a search
 * dropdown and a notification drawer are `Popover` and `Combobox`, not this.
 *
 * ── The one place the lock-don't-hide rule inverts ─────────────────────────
 *
 * A capability the viewer does not hold is normally **locked with a stated
 * reason, not hidden** (`00-`'s cross-cutting rule, `FR-PERM-012` AC1), which is
 * why `Button` carries `disabledReason`. **`Menu` is the documented exception**:
 * `FR-COLLAB-002` AC2, `FR-COLLAB-002` AC3 and `FR-COLLAB-003` AC3 all say the
 * option is *"not offered"*, so an item the viewer may not use is **omitted**,
 * never disabled. `Menu` therefore takes **no `disabledReason`**, and adding one
 * would contradict three acceptance criteria at once.
 *
 * `MenuItem.disabled` survives for the other kind of refusal — an action that is
 * momentarily unavailable rather than one the viewer is not entitled to. It is
 * **not** a permission signal; do not "fix" it toward `disabledReason`.
 *
 * ── Selection ─────────────────────────────────────────────────────────────
 *
 * A section that marks a current choice declares `selection: 'radio'` and
 * carries `value` — Radix then renders `role="menuitemradio"` with a real
 * `aria-checked`. It is declared per section rather than inferred from the items,
 * because a section that mixed `checked: false` with `checked: undefined` would
 * silently pick a different ARIA model, which is the accidental-pattern failure
 * this chunk exists to prevent.
 *
 * `footerItems` sit **outside** every radio group, after a separator:
 * "New workspace…" is not a workspace (`entry-surfaces.md`), and a plain
 * `role="menuitem"` is what says so.
 */

export interface MenuItem {
  /** Stable key, and the radio value inside a `selection: 'radio'` section. */
  id: string;
  label: ReactNode;
  onSelect: () => void;
  /** Rendered before the label and hidden from the accessibility tree. */
  icon?: ReactNode;
  /** Paints with `--color-bad`. Delete, Remove, Leave. */
  destructive?: boolean;
  /**
   * Temporarily unavailable — **not** a permission refusal. See the note above:
   * a capability the viewer does not hold is omitted from `items` entirely.
   */
  disabled?: boolean;
  /** Keep the menu open after selection, for in-menu preference controls. */
  keepOpenOnSelect?: boolean;
  /**
   * Displayed, never bound. Keyboard shortcuts are `B41`'s
   * (B3-BRIEF §8) and this primitive registers nothing.
   */
  shortcut?: string;
}

export interface MenuSection {
  id: string;
  /** Optional heading; becomes the group's `aria-labelledby`. */
  heading?: string;
  items: MenuItem[];
  /** Renders the section as a radio group with real `aria-checked` items. */
  selection?: 'radio';
  /** The `MenuItem.id` currently chosen. Only meaningful with `selection: 'radio'`. */
  value?: string;
  onValueChange?: (id: string) => void;
}

interface MenuBaseProps {
  /**
   * The control that opens the menu, through Radix's `asChild` — so it must be a
   * single element that accepts a ref, and it supplies its own accessible name
   * (`<IconButton label="Comment actions" …/>`). The primitive adds
   * `aria-haspopup`, `aria-expanded` and `aria-controls`.
   */
  trigger: ReactNode;
  /** Actions outside every radio group, after a separator. */
  footerItems?: MenuItem[];
  align?: 'start' | 'end';
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /**
   * Called as the menu closes, before focus is restored to the trigger.
   * `event.preventDefault()` cancels that restoration.
   *
   * ⚠ Added by `C2`, for one narrow and real case: **a menu item that opens
   * something else the member is then supposed to be typing in.** Radix
   * returns focus to the trigger on close, which is exactly right for an item
   * that acts in place (`Archive this list`) and exactly wrong for one that
   * hands over — `FR-BOARD-009` AC3's `Add a list to the right` opens a
   * composer, and without this the composer opens with focus sitting back on
   * the menu button. Nothing is red; a keyboard member simply types into
   * nothing, which is the class `FR-EXP-022` exists to prevent.
   *
   * Restoring focus to the trigger is the accessible default and stays the
   * default (`FR-EXP-022` AC3, asserted in `Menu.test.tsx`). Pass this ONLY
   * when the item has moved focus somewhere deliberate, and make sure it
   * actually has — cancelling the restoration without giving focus a new home
   * drops it to `<body>`, which is worse than either behaviour.
   */
  onCloseAutoFocus?: (event: Event) => void;
  className?: string;
}

/**
 * `items` and `sections` are the same shape at two depths — a flat list is the
 * common case and `[{ id: 'items', items }]` is noise at every call site — so
 * exactly one of them is accepted, enforced by the type rather than by a
 * runtime check.
 */
export type MenuProps = MenuBaseProps &
  ({ items: MenuItem[]; sections?: never } | { sections: MenuSection[]; items?: never });

const itemClasses = (item: MenuItem) =>
  cn(
    'flex w-full cursor-pointer items-center gap-2 rounded-sm px-2.5 py-1.5 text-card text-ink select-none',
    // `--color-border` as a highlight *fill* is legitimate — the token's
    // "decorative only" note is about edges that delineate a control, and this
    // is the one fill that reads in both themes against `--color-surface-raised`.
    'data-[highlighted]:bg-border data-[disabled]:cursor-default data-[disabled]:text-ink-muted',
    item.destructive && 'text-bad',
  );

function ItemBody({ item }: { item: MenuItem }) {
  return (
    <>
      {item.icon ? (
        <span aria-hidden="true" className="shrink-0">
          {item.icon}
        </span>
      ) : null}
      <span className="min-w-0 flex-1 truncate">{item.label}</span>
      {item.shortcut ? (
        // Display only — see `MenuItem.shortcut`. On an opaque menu surface, so
        // `--color-ink-muted` is legal here.
        <span className="shrink-0 text-xs text-ink-muted">{item.shortcut}</span>
      ) : null}
    </>
  );
}

function handleItemSelect(item: MenuItem, event: Event): void {
  if (item.keepOpenOnSelect === true) event.preventDefault();
  item.onSelect();
}

export function Menu({
  trigger,
  items,
  sections,
  footerItems,
  align = 'start',
  open,
  onOpenChange,
  onCloseAutoFocus,
  className,
}: MenuProps) {
  const baseId = useId();
  const resolved: MenuSection[] = sections ?? [{ id: 'items', items: items ?? [] }];

  // Spread conditionally rather than passing `undefined`: Radix declares
  // `open?: boolean` without `| undefined`, and `exactOptionalPropertyTypes` is
  // on — passing the key at all is what puts Radix into controlled mode.
  const rootProps = {
    ...(open === undefined ? {} : { open }),
    ...(onOpenChange === undefined ? {} : { onOpenChange }),
  };

  return (
    <MenuPrimitive.Root {...rootProps}>
      <MenuPrimitive.Trigger asChild>{trigger}</MenuPrimitive.Trigger>
      <MenuPrimitive.Portal>
        <MenuPrimitive.Content
          align={align}
          {...(onCloseAutoFocus ? { onCloseAutoFocus } : {})}
          sideOffset={6}
          collisionPadding={12}
          className={cn(
            'z-50 min-w-[12rem] max-w-[20rem] overflow-hidden rounded-card border border-border-strong bg-surface-raised p-1 text-ink shadow-card',
            className,
          )}
        >
          {resolved.map((section, index) => {
            const headingId = `${baseId}-${section.id}-heading`;
            const heading = section.heading ? (
              <MenuPrimitive.Label
                id={headingId}
                className="truncate px-2.5 py-1.5 text-xs text-ink-muted"
              >
                {section.heading}
              </MenuPrimitive.Label>
            ) : null;

            const body =
              section.selection === 'radio' ? (
                <MenuPrimitive.RadioGroup
                  value={section.value ?? ''}
                  onValueChange={section.onValueChange ?? (() => undefined)}
                  aria-labelledby={section.heading ? headingId : undefined}
                >
                  {section.items.map((item) => (
                    <MenuPrimitive.RadioItem
                      key={item.id}
                      value={item.id}
                      disabled={item.disabled ?? false}
                      onSelect={(event) => handleItemSelect(item, event)}
                      className={itemClasses(item)}
                    >
                      {/*
                        ⚠ **The checked state was `aria-checked` and nothing
                        else until chunk `D2`**, which is the first chunk with
                        any consumer of `selection: 'radio'` at all — `B3` built
                        the capability speculatively and nothing called it. A
                        sighted member opening the workspace switcher could not
                        see which workspace was current, and a screen-reader
                        user could; that asymmetry is the defect, and it is
                        fixed here in the primitive rather than worked around in
                        each of the two callers `D2` adds.

                        The slot is reserved on **every** radio item, checked or
                        not, so the labels align in a column instead of shifting
                        sideways as the selection moves.
                      */}
                      <span
                        aria-hidden="true"
                        className="flex size-4 shrink-0 items-center"
                      >
                        <MenuPrimitive.ItemIndicator>
                          <Check className="size-4" />
                        </MenuPrimitive.ItemIndicator>
                      </span>
                      <ItemBody item={item} />
                    </MenuPrimitive.RadioItem>
                  ))}
                </MenuPrimitive.RadioGroup>
              ) : (
                <MenuPrimitive.Group
                  aria-labelledby={section.heading ? headingId : undefined}
                >
                  {section.items.map((item) => (
                    <MenuPrimitive.Item
                      key={item.id}
                      disabled={item.disabled ?? false}
                      onSelect={(event) => handleItemSelect(item, event)}
                      className={itemClasses(item)}
                    >
                      <ItemBody item={item} />
                    </MenuPrimitive.Item>
                  ))}
                </MenuPrimitive.Group>
              );

            return (
              <Fragment key={section.id}>
                {index > 0 ? (
                  <MenuPrimitive.Separator className="my-1 h-px bg-border" />
                ) : null}
                {heading}
                {body}
              </Fragment>
            );
          })}

          {footerItems && footerItems.length > 0 ? (
            <>
              <MenuPrimitive.Separator className="my-1 h-px bg-border" />
              {footerItems.map((item) => (
                <MenuPrimitive.Item
                  key={item.id}
                  disabled={item.disabled ?? false}
                  onSelect={(event) => handleItemSelect(item, event)}
                  className={itemClasses(item)}
                >
                  <ItemBody item={item} />
                </MenuPrimitive.Item>
              ))}
            </>
          ) : null}
        </MenuPrimitive.Content>
      </MenuPrimitive.Portal>
    </MenuPrimitive.Root>
  );
}
